/** `getBoundingClientRect()` のうち判定に使う部分だけの型（テストでオブジェクトを直接渡せるように） */
export interface ClientRectLike {
  left: number
  top: number
  width: number
  height: number
}

/**
 * ポインタ座標 (clientX, clientY) が、`<img>` に**実際に絵が描かれている範囲**の上にあるかを返す
 * （issue #2439: 写真の外でのホイールズーム抑止）。
 *
 * - `rect` は `img.getBoundingClientRect()`。CSS transform（react-zoom-pan-pinch の拡大縮小・移動）が
 *   反映済みのスクリーン座標なので、ズーム後の位置・倍率を別途計算する必要はない
 * - `<img>` は `object-contain` で描画しているため、要素の矩形と絵の範囲がずれる（レターボックス）ことがある。
 *   自然サイズ (naturalWidth / naturalHeight) のアスペクト比から、矩形の中で中央に置かれた絵の範囲を求める
 * - 自然サイズが取れない（読み込み前など）ときは、レターボックス補正なしで要素の矩形そのもので判定する
 */
export const isPointOverRenderedImage = (
  rect: ClientRectLike,
  naturalWidth: number,
  naturalHeight: number,
  clientX: number,
  clientY: number,
): boolean => {
  if (rect.width <= 0 || rect.height <= 0) return false

  let drawnWidth = rect.width
  let drawnHeight = rect.height
  if (naturalWidth > 0 && naturalHeight > 0) {
    const imageRatio = naturalWidth / naturalHeight
    const boxRatio = rect.width / rect.height
    if (imageRatio > boxRatio) {
      // 画像の方が横長 → 幅いっぱい・上下に余白
      drawnHeight = rect.width / imageRatio
    } else {
      // 画像の方が縦長（または同じ比率） → 高さいっぱい・左右に余白
      drawnWidth = rect.height * imageRatio
    }
  }

  const left = rect.left + (rect.width - drawnWidth) / 2
  const top = rect.top + (rect.height - drawnHeight) / 2
  return (
    clientX >= left &&
    clientX <= left + drawnWidth &&
    clientY >= top &&
    clientY <= top + drawnHeight
  )
}
